/**
 * Postcode Validation System
 * Handles header postcode validation and modal interactions
 * Uses custom Shopify logistics app API for postcode validation
 */

class PostcodeValidator {
  constructor() {
    // Don't initialize on cart page at all
    if (this.isCartPage()) {
      console.log('Postcode validator - Skipping initialization on cart page');
      return;
    }
    
    this.form = document.getElementById('postcode-form');
    this.input = document.getElementById('postcode-input');
    this.submitButton = document.querySelector('#postcode-form button[type="submit"]');
    this.submitText = document.querySelector('.postcode-submit__text');
    this.result = document.getElementById('postcode-result');
    this.resultMessage = document.querySelector('.postcode-result__message');
    this.resultLoading = document.querySelector('.postcode-result__loading');
    this.postcodeDisplay = document.getElementById('postcode-display');
    this.postcodeDisplayMobile = document.getElementById('postcode-display-mobile');
    
    // Debug: Check if all elements are found
    console.log('PostcodeValidator elements check:');
    console.log('form:', !!this.form);
    console.log('input:', !!this.input);
    console.log('submitButton:', !!this.submitButton);
    console.log('submitText:', !!this.submitText);
    console.log('result:', !!this.result);
    console.log('resultMessage:', !!this.resultMessage);
    console.log('resultLoading:', !!this.resultLoading);
    
    this.init();
  }
  
  isCartPage() {
    // Check if we're on the cart page
    return window.location.pathname === '/cart' || 
           window.location.pathname === '/cart/' ||
           document.querySelector('form#cart') !== null ||
           document.querySelector('.cart__contents') !== null;
  }

  init() {
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.input.addEventListener('input', this.handleInput.bind(this));
      
      // Show initial state
      this.showInitialState();
      
      // Load saved postcode on page load
      this.loadSavedPostcode();
      
      // Add modal close functionality
      this.setupModalClose();
      
      // Debug modal opener
      this.debugModalOpener();
    }
  }
  
  showInitialState() {
    // Show result area with initial message
    if (this.result) {
      this.result.style.display = 'block';
    }
    if (this.resultMessage) {
      this.resultMessage.textContent = 'Check your postcode';
      this.resultMessage.style.display = 'inline';
    }
    // Hide loading spinner
    if (this.resultLoading) this.resultLoading.style.display = 'none';
    // Remove state classes
    if (this.result) {
      this.result.classList.remove('postcode-result--success', 'postcode-result--error');
    }
  }

  debugModalOpener() {
    const postcodeContainers = document.querySelectorAll('.postcode_container');
    console.log('Found postcode containers:', postcodeContainers.length);
    
    if (postcodeContainers.length === 0) {
      console.error('No postcode containers found!');
      return;
    }
    
    postcodeContainers.forEach((container, index) => {
      console.log(`Postcode container ${index}:`, container);
      console.log(`Data modal:`, container.getAttribute('data-modal'));
      
      const modal = document.querySelector(container.getAttribute('data-modal'));
      console.log(`Target modal:`, modal);
      
      if (!modal) {
        console.error(`Modal not found for container ${index}`);
        return;
      }
      
      // Add click listener to debug
      container.addEventListener('click', (event) => {
        console.log('Postcode container clicked!', event.target);
        console.log('Postcode container element:', container);
        console.log('Event type:', event.type);
        console.log('Event bubbles:', event.bubbles);
        
        // Try to manually show the modal
        if (modal) {
          console.log('Attempting to show modal...');
          console.log('Modal show method:', typeof modal.show);
          if (typeof modal.show === 'function') {
            modal.show(container);
            console.log('Modal show called');
            console.log('Modal has open attribute:', modal.hasAttribute('open'));
            console.log('Modal computed style display:', window.getComputedStyle(modal).display);
          } else {
            console.log('Modal does not have show method');
            // Fallback: manually set open attribute
            modal.setAttribute('open', '');
            document.body.classList.add('overflow-hidden');
            console.log('Manually opened modal');
          }
        } else {
          console.error('Modal is null!');
        }
      });
      
      console.log(`Click listener added to container ${index}`);
    });
  }

  setupModalClose() {
    const modal = document.getElementById('PostcodeModal');
    
    if (modal) {
      // Listen for the theme's modal close event
      modal.addEventListener('modalClosed', () => {
        this.clearForm();
      });
      
      // Close modal when clicking outside the modal content
      modal.addEventListener('click', (event) => {
        // Check if the click is on the modal backdrop (not on the content)
        const modalContent = modal.querySelector('.postcode-modal__content');
        if (modalContent && !modalContent.contains(event.target)) {
          this.closeModal();
        }
      });
    }
  }
  
  closeModal() {
    const modal = document.getElementById('PostcodeModal');
    if (modal) {
      // Check if modal has a close method (Shopify theme modal-dialog)
      if (typeof modal.close === 'function') {
        modal.close();
      } else if (typeof modal.hide === 'function') {
        modal.hide();
      } else {
        // Fallback: manually close by removing open attribute
        modal.removeAttribute('open');
        modal.style.display = 'none';
        document.body.classList.remove('overflow-hidden');
      }
      this.clearForm();
    }
  }

  clearForm() {
    // Clear form and reset to initial state when modal closes
    if (this.input) {
      this.input.value = '';
    }
    this.showInitialState();
  }

  handleInput(event) {
    // Format postcode as user types (basic formatting)
    let value = event.target.value.toUpperCase().replace(/[^A-Z0-9\s]/g, '');
    event.target.value = value;
  }

  async handleSubmit(event) {
    event.preventDefault();
    
    const postcode = this.input.value.trim();
    if (!postcode) {
      this.showInitialState();
      return;
    }

    this.setLoading(true);
    this.showLoadingState();

    try {
      // Validate postcode using custom app API
      const validationResult = await this.validatePostcodeAPI(postcode);
      
      if (validationResult.valid) {
        this.showSuccess('Delivery available to this postcode!', postcode);
        this.savePostcode(postcode);
      } else {
        this.showError('Sorry, we don\'t deliver to this postcode yet.');
      }
    } catch (error) {
      console.error('Postcode validation error:', error);
      this.showError('Unable to check postcode. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }
  
  showLoadingState() {
    // Show loading spinner in result area
    if (this.result) {
      this.result.style.display = 'block';
    }
    if (this.resultLoading) {
      this.resultLoading.style.display = 'flex';
    }
    if (this.resultMessage) {
      this.resultMessage.style.display = 'none';
    }
    // Remove state classes
    if (this.result) {
      this.result.classList.remove('postcode-result--success', 'postcode-result--error');
    }
  }

  async validatePostcodeAPI(postcode) {
    try {
      console.log('Postcode validator - Validating postcode via API:', postcode);
      
      const headers = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      
      const requestBody = { 
        postcode: postcode,
        pathway: 'internal__postcode_validation'
      };
      
      console.log('Postcode validator - Request body:', requestBody);
      
      const response = await fetch('/apps/logistics/windows', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody)
      });

      // Check Content-Type first - treat non-JSON as failure even if status is 200
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const responseText = await response.text();
        console.error('Postcode validator - Received non-JSON response (API failure):', {
          contentType: contentType,
          status: response.status,
          statusText: response.statusText,
          responsePreview: responseText.substring(0, 500)
        });
        throw new Error(`API returned HTML instead of JSON (status: ${response.status}). The app proxy may not be configured correctly or the endpoint is returning an error page.`);
      }

      if (!response.ok) {
        // If we get here, it's JSON but with an error status
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(`HTTP error! status: ${response.status}, message: ${JSON.stringify(errorData)}`);
      }

      const data = await response.json();
      console.log('Postcode validator - API response:', data);
      
      // Expect response format: { valid: true } or { valid: false }
      return {
        valid: data.valid === true
      };
    } catch (error) {
      console.error('Postcode validator - API validation error:', error);
      // Return invalid on error to be safe
      return { valid: false };
    }
  }

  setLoading(loading) {
    if (this.submitButton) {
      if (loading) {
        this.submitButton.disabled = true;
        this.submitButton.style.opacity = '0.5';
      } else {
        this.submitButton.disabled = false;
        this.submitButton.style.opacity = '1';
      }
    }
  }

  showSuccess(message, postcode) {
    // Hide loading spinner
    if (this.resultLoading) {
      this.resultLoading.style.display = 'none';
    }
    // Show message
    if (this.resultMessage) {
      this.resultMessage.textContent = message;
      this.resultMessage.style.display = 'inline';
    }
    // Ensure result is visible and styled
    if (this.result) {
      this.result.style.display = 'block';
      this.result.classList.remove('postcode-result--error');
      this.result.classList.add('postcode-result--success');
    }
    
    // Update display text
    this.updatePostcodeDisplay(postcode);
  }

  showError(message) {
    // Hide loading spinner
    if (this.resultLoading) {
      this.resultLoading.style.display = 'none';
    }
    // Show message
    if (this.resultMessage) {
      this.resultMessage.textContent = message;
      this.resultMessage.style.display = 'inline';
    }
    // Ensure result is visible and styled
    if (this.result) {
      this.result.style.display = 'block';
      this.result.classList.remove('postcode-result--success');
      this.result.classList.add('postcode-result--error');
    }
  }

  hideResult() {
    // This is now replaced by showInitialState
    this.showInitialState();
  }

  updatePostcodeDisplay(postcode) {
    if (this.postcodeDisplay) {
      this.postcodeDisplay.textContent = postcode;
    }
    if (this.postcodeDisplayMobile) {
      this.postcodeDisplayMobile.textContent = postcode;
    }
  }

  savePostcode(postcode) {
    try {
      localStorage.setItem('validated_postcode', postcode);
    } catch (error) {
      console.warn('Could not save postcode to localStorage:', error);
    }
  }

  loadSavedPostcode() {
    try {
      const savedPostcode = localStorage.getItem('validated_postcode');
      if (savedPostcode) {
        this.updatePostcodeDisplay(savedPostcode);
      }
    } catch (error) {
      console.warn('Could not load saved postcode:', error);
    }
  }
}

/**
 * Legacy Metafield Support (Deprecated)
 * This function is kept for backward compatibility but is no longer used
 * Postcode validation now uses the custom app API
 */
function loadPostcodeMetafields(metafieldData) {
  console.warn('loadPostcodeMetafields is deprecated - postcode validation now uses API');
  // Keep for backward compatibility but don't use
}

// Initialize postcode validator when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new PostcodeValidator();
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { PostcodeValidator, loadPostcodeMetafields };
}

// Make functions globally available
window.PostcodeValidator = PostcodeValidator;
window.loadPostcodeMetafields = loadPostcodeMetafields; // Deprecated but kept for compatibility
